/**
 * A crew's papers, and what they say.
 *
 * Jonathon, 2026-08-27: "add a Documents section to each crew record with file
 * upload ... on upload, parse the document (COI, W-9, etc.) and auto-populate
 * / update the fields above. Show extracted values for review before saving."
 *
 * So this screen is in three parts, in the order somebody uses them:
 *
 *  1. **What we hold today** — workers' comp, general liability, their expiry
 *     dates, and which kind of tax number is on the W-9. All editable by hand,
 *     because they were before this existed and a certificate that will not
 *     read is not a reason to be stuck.
 *  2. **A file, uploaded.** It is stored first and read second, always: a
 *     certificate that reached the bucket is one the office has, whatever the
 *     reader then makes of it.
 *  3. **What the reader made of it**, field by field, with the ones it was
 *     unsure of marked — and nothing is written to the crew until somebody
 *     presses the button. That is the whole reason the review step exists.
 *
 * The tax number is never here, because it is never anywhere: the reader is
 * asked only which kind the form gives, and `crewExtractionPatch` in core
 * would drop it if it came back anyway.
 */

import {
  CREW_DOCUMENT_KINDS,
  CREW_DOCUMENT_LABELS,
  crewCoverage,
  crewDocumentKind,
  crewExtractionPatch,
  type Crew,
  type CrewDocumentKind,
  type CrewPatch,
  crewLabel,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal, Row } from "../../components/ui";
import { t } from "../../i18n";
import {
  deleteCrewDocument,
  fetchCrewDocuments,
  readCrewDocument,
  recordCrewDocument,
  signCrewDocument,
  uploadCrewDocument,
} from "../../lib/crewDocuments";
import { useAsync } from "../../lib/hooks";
import { updateRow } from "../../lib/queries";
import { useSession } from "../../session";
import { CREW_FIELD_LABELS, coverageText } from "./NewCrewModal";

/** Today, as the columns hold it. */
const today = (): string => new Date().toISOString().slice(0, 10);

const readable = (value: unknown): string => {
  if (value === true) return t("Yes");
  if (value === false) return t("No");
  if (value == null || value === "") return "—";
  return String(value);
};

export function CrewDocumentsModal({
  crew,
  onClose,
  onSaved,
}: {
  crew: Crew;
  onClose: () => void;
  /** The crews list re-reads: the coverage column is built from these fields. */
  onSaved: () => Promise<void>;
}) {
  const { profileId } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [kind, setKind] = useState<CrewDocumentKind>("coi");
  /** The reading waiting to be confirmed, and the file it came from. */
  const [review, setReview] = useState<{
    filename: string;
    storagePath: string;
    kind: CrewDocumentKind;
    extraction: Record<string, unknown>;
    patch: CrewPatch;
  } | null>(null);

  const documents = useAsync(() => fetchCrewDocuments(crew.id), [crew.id]);
  const coverage = crewCoverage(crew, today());

  const guard = async (label: string, action: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(null);
    }
  };

  const save = (patch: Record<string, unknown>) =>
    void guard("save", async () => {
      await updateRow("crews", crew.id, patch);
      await onSaved();
    });

  /**
   * Upload, then read.
   *
   * A failure to read is reported and the document is still recorded — the
   * office has the certificate either way, and the fields can be typed.
   */
  const upload = (file: File) =>
    void guard("upload", async () => {
      const storagePath = await uploadCrewDocument(crew.id, file);
      let extraction: Record<string, unknown> | null = null;
      let readError: string | null = null;
      try {
        extraction = await readCrewDocument(file);
      } catch (caught) {
        readError = caught instanceof Error ? caught.message : String(caught);
      }

      if (!extraction) {
        await recordCrewDocument({
          crewId: crew.id,
          kind,
          filename: file.name,
          storagePath,
          extraction: null,
          expiresOn: null,
          uploadedBy: profileId ?? null,
        });
        await documents.reload();
        setError(readError);
        return;
      }

      // What the reader says it is, unless it could not tell — then what the
      // person who uploaded it said, which is better than "other".
      const read = crewDocumentKind(extraction.document_kind);
      setReview({
        filename: file.name,
        storagePath,
        kind: read === "other" ? kind : read,
        extraction,
        patch: crewExtractionPatch(extraction),
      });
    });

  /** Keep the reading: write the fields it found, and record the document. */
  const confirm = () =>
    void guard("confirm", async () => {
      if (!review) return;
      const patch = review.patch;
      if (Object.keys(patch).length > 0) {
        await updateRow("crews", crew.id, patch as Record<string, unknown>);
      }
      await recordCrewDocument({
        crewId: crew.id,
        kind: review.kind,
        filename: review.filename,
        storagePath: review.storagePath,
        extraction: review.extraction,
        // The date this document itself runs out — the earlier of the two it
        // named, which is the one that lapses first.
        expiresOn:
          [patch.wc_expires_on, patch.gl_expires_on]
            .filter((day): day is string => Boolean(day))
            .sort()[0] ?? null,
        uploadedBy: profileId ?? null,
      });
      setReview(null);
      await documents.reload();
      await onSaved();
    });

  /** Keep the document, change nothing. */
  const discard = () =>
    void guard("discard", async () => {
      if (!review) return;
      await recordCrewDocument({
        crewId: crew.id,
        kind: review.kind,
        filename: review.filename,
        storagePath: review.storagePath,
        extraction: review.extraction,
        expiresOn: null,
        uploadedBy: profileId ?? null,
      });
      setReview(null);
      await documents.reload();
    });

  const open = (path: string) =>
    void guard("open", async () => {
      const url = await signCrewDocument(path);
      window.open(url, "_blank", "noopener");
    });

  /**
   * Throw one away — anybody in the office, not only an admin.
   *
   * Jonathon, 2026-09-20: "everyone needs to be able to delete docs in the
   * crews docs section." The wrong certificate on the wrong crew is an
   * everyday mistake and the person who made it is the person standing here.
   *
   * It does not touch the fields above. A date typed off a certificate is the
   * office's answer to "is this crew insured", and deleting the scan it was
   * read from does not unanswer it — if the answer was wrong too, it is
   * changed in What we hold, where it was set.
   */
  const remove = (document: {
    id: string;
    filename: string;
    storage_path: string;
  }) =>
    void guard("delete", async () => {
      if (
        !window.confirm(
          t(
            "Delete “{name}” for good? The file goes with it, and nothing here can put it back.",
            { name: document.filename },
          ),
        )
      ) {
        return;
      }
      // Reload whether or not the file went. The row goes first, so by the
      // time the bucket refuses, the list is already showing a document the
      // database no longer has. The error is still shown; `guard` has it.
      try {
        await deleteCrewDocument(document);
      } finally {
        await documents.reload();
      }
    });

  const confidence = (review?.extraction.confidence ?? {}) as Record<
    string,
    string
  >;

  return (
    <Modal
      title={t("{crew} — documents", { crew: crewLabel(crew) ?? crew.name })}
      subtitle={t(
        "Certificates and W-9s. Uploading one fills these fields in for you to check.",
      )}
      onClose={onClose}
      wide
      actions={
        <button className="btn-primary" onClick={onClose}>
          {t("Done")}
        </button>
      }
    >
      <ErrorNote error={error} />

      <div className={`crewcov ${coverage.state}`} role="status">
        <b>{coverageText(coverage)}</b>
        {coverage.state === "expired" || coverage.state === "expiring" ? (
          <span>{t(" — ask this crew for a current certificate.")}</span>
        ) : null}
      </div>

      <div className="chk-sub">{t("What we hold")}</div>
      <Row label={t("Workers' comp")}>
        <select
          aria-label={t("Workers' comp")}
          value={crew.has_wc == null ? "" : crew.has_wc ? "yes" : "no"}
          disabled={busy !== null}
          onChange={(e) =>
            save({
              has_wc: e.target.value === "" ? null : e.target.value === "yes",
            })
          }
        >
          <option value="">{t("Not recorded")}</option>
          <option value="yes">{t("Carries it")}</option>
          <option value="no">{t("Does not")}</option>
        </select>
        <input
          type="date"
          aria-label={t("Workers' comp expires")}
          value={crew.wc_expires_on ?? ""}
          disabled={busy !== null}
          onChange={(e) => save({ wc_expires_on: e.target.value || null })}
        />
      </Row>
      <Row label={t("General liability")}>
        <select
          aria-label={t("General liability")}
          value={crew.has_gl == null ? "" : crew.has_gl ? "yes" : "no"}
          disabled={busy !== null}
          onChange={(e) =>
            save({
              has_gl: e.target.value === "" ? null : e.target.value === "yes",
            })
          }
        >
          <option value="">{t("Not recorded")}</option>
          <option value="yes">{t("Carries it")}</option>
          <option value="no">{t("Does not")}</option>
        </select>
        <input
          type="date"
          aria-label={t("General liability expires")}
          value={crew.gl_expires_on ?? ""}
          disabled={busy !== null}
          onChange={(e) => save({ gl_expires_on: e.target.value || null })}
        />
      </Row>
      <Row label={t("Tax ID on file")}>
        <label className="inline">
          <input
            type="checkbox"
            aria-label={t("EIN on file")}
            checked={crew.has_ein === true}
            disabled={busy !== null}
            onChange={(e) => save({ has_ein: e.target.checked })}
          />
          EIN
        </label>
        <label className="inline">
          <input
            type="checkbox"
            aria-label={t("Social Security number on file")}
            checked={crew.has_social === true}
            disabled={busy !== null}
            onChange={(e) => save({ has_social: e.target.checked })}
          />
          {t("Social")}
        </label>
      </Row>
      <p className="na-hint">
        {t(
          "Which kind of number the W-9 gives. The number itself is never stored — it stays on the document.",
        )}
      </p>

      {/* ------------------------------------------------------ upload --- */}

      {review === null && (
        <>
          <div className="chk-sub">{t("Add a document")}</div>
          <Row label={t("It is a")}>
            <select
              aria-label={t("Document type")}
              value={kind}
              disabled={busy !== null}
              onChange={(e) => setKind(e.target.value as CrewDocumentKind)}
            >
              {CREW_DOCUMENT_KINDS.map((value) => (
                <option key={value} value={value}>
                  {t(CREW_DOCUMENT_LABELS[value])}
                </option>
              ))}
            </select>
          </Row>
          <input
            type="file"
            aria-label={t("Upload a document")}
            accept="application/pdf,image/*"
            disabled={busy !== null}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) upload(file);
            }}
          />
          <p className="na-hint">
            {busy === "upload"
              ? t("Reading it…")
              : t("A PDF or a photograph. It is saved first, then read.")}
          </p>
        </>
      )}

      {/* ------------------------------------------------------ review --- */}

      {review && (
        <>
          <div className="chk-sub">{t("What the document says")}</div>
          <p className="na-hint">
            {t(
              "Nothing has been written to this crew yet. Check it, then keep it.",
            )}
          </p>
          <div className="ewrap">
            <table className="etable">
              <thead>
                <tr>
                  <th>{t("Field")}</th>
                  <th>{t("Reading")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {Object.keys(CREW_FIELD_LABELS).map((field) => {
                  const value = review.extraction[field];
                  if (value == null || value === "") return null;
                  return (
                    <tr key={field}>
                      <td>{t(CREW_FIELD_LABELS[field] ?? field)}</td>
                      <td>{readable(value)}</td>
                      <td className="sub">
                        {confidence[field] === "low" ? (
                          <span className="ins expired">{t("check this")}</span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="actions">
            <button
              className="btn-primary"
              disabled={busy !== null}
              onClick={confirm}
            >
              {t("Keep these")}
            </button>
            <button
              className="btn-ghost"
              disabled={busy !== null}
              onClick={discard}
            >
              {t("Keep the file only")}
            </button>
          </div>
        </>
      )}

      {/* -------------------------------------------------- what is on file --- */}

      <div className="chk-sub">{t("On file")}</div>
      {(documents.data ?? []).length === 0 ? (
        <p className="na-hint">{t("Nothing uploaded yet.")}</p>
      ) : (
        <div className="ewrap">
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Uploaded")}</th>
                <th>{t("Kind")}</th>
                <th>{t("File")}</th>
                <th>{t("Expires")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(documents.data ?? []).map((document) => (
                <tr key={document.id}>
                  <td className="sub">{document.created_at.slice(0, 10)}</td>
                  <td className="sub">
                    {t(CREW_DOCUMENT_LABELS[crewDocumentKind(document.kind)])}
                  </td>
                  <td>
                    <button
                      className="linkish"
                      disabled={busy !== null}
                      onClick={() => open(document.storage_path)}
                    >
                      {document.filename}
                    </button>
                  </td>
                  <td className="sub">{document.expires_on ?? "—"}</td>
                  <td>
                    <button
                      type="button"
                      className="xbtn"
                      aria-label={t("Delete {name}", {
                        name: document.filename,
                      })}
                      title={t("Delete it and its file for good")}
                      disabled={busy !== null}
                      onClick={() => remove(document)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
